import React, { useRef, useState, useEffect } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

export interface VirtualizedGridProps<T> {
  /** All items to render in the grid */
  items: readonly T[];
  /** Number of columns in the grid */
  columns: number;
  /** Estimated height of each row in pixels */
  estimatedRowHeight: number;
  /** Aspect ratio for empty cells (e.g., '1/1' or '16/9') */
  emptyAspectRatio: string;
  /** Scroll margin offset */
  scrollMargin?: number;
  /** Number of rows to render outside viewport */
  overscan?: number;
  /** Render function for each item */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Get unique key for each item */
  getItemKey: (item: T, index: number) => string;
}

/**
 * Skeleton placeholder component for fast scrolling
 * Shows gray placeholder instead of blank white space (YouTube-style)
 */
const SkeletonPlaceholder: React.FC<{ aspectRatio: string }> = ({ aspectRatio }) => (
  <div
    className="animate-pulse bg-gray-200 rounded"
    style={{ aspectRatio }}
  />
);

/**
 * Generic virtualized grid component
 * Handles window-based virtualization for large grids
 * 
 * Used by both VirtualizedImagesSection and VirtualizedVideosSection
 * to eliminate code duplication in the grid rendering logic
 */
function VirtualizedGrid<T>({
  items,
  columns,
  estimatedRowHeight,
  emptyAspectRatio,
  scrollMargin: externalScrollMargin,
  overscan = 15, // Increased to 15 for smoother fast scrolling (YouTube uses ~15-20)
  renderItem,
  getItemKey,
}: Readonly<VirtualizedGridProps<T>>): React.ReactElement {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(externalScrollMargin ?? 0);

  // Calculate total rows
  const totalRows = Math.ceil(items.length / columns);

  // Track scroll margin after mount - use useEffect instead of useLayoutEffect
  // to avoid flushSync warnings from react-virtual
  useEffect(() => {
    if (listRef.current && externalScrollMargin === undefined) {
      setScrollMargin(listRef.current.offsetTop);
    }
  }, [externalScrollMargin]);

  // Window virtualizer for efficient rendering
  // Higher overscan prevents blank screens during fast scrolling
  const rowVirtualizer = useWindowVirtualizer({
    count: totalRows,
    estimateSize: () => estimatedRowHeight,
    overscan,
    scrollMargin,
    // Enable smooth scrolling optimization
    measureElement: (element) => element?.getBoundingClientRect().height
  });

  const virtualRows = rowVirtualizer.getVirtualItems();

  return (
    <div
      ref={listRef}
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: '100%',
        position: 'relative',
      }}
    >
      {virtualRows.map((virtualRow) => {
        const rowIndex = virtualRow.index;
        const startIndex = rowIndex * columns;
        const endIndex = Math.min(startIndex + columns, items.length);
        const rowItems = items.slice(startIndex, endIndex);

        // If items haven't loaded yet (during fast scroll), show skeletons
        const showSkeletons = rowItems.length === 0 && rowIndex < totalRows;

        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start - scrollMargin}px)`,
              paddingBottom: '20px',
            }}
          >
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
            >
              {showSkeletons ? (
                // Show skeleton placeholders during fast scrolling (YouTube-style)
                Array.from({ length: columns }).map((_, index) => (
                  <SkeletonPlaceholder
                    key={`skeleton-${rowIndex}-${index}`}
                    aspectRatio={emptyAspectRatio}
                  />
                ))
              ) : (
                <>
                  {rowItems.map((item, idx) => (
                    <React.Fragment key={getItemKey(item, startIndex + idx)}>
                      {renderItem(item, startIndex + idx)}
                    </React.Fragment>
                  ))}

                  {/* Fill empty cells in last row */}
                  {rowItems.length < columns &&
                    Array.from({ length: columns - rowItems.length }).map((_, index) => (
                      <div key={`empty-${rowIndex}-${index}`} style={{ aspectRatio: emptyAspectRatio }} />
                    ))
                  }
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default VirtualizedGrid;
